import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
    Loader2, Users, TrendingUp, Award, PoundSterling, BarChart3,
    ArrowLeft, Send, CheckCircle, XCircle, Clock, Package,
    CalendarDays, FileText, User, Mail, MapPin, Download, Eye,
    LayoutGrid, Table as TableIcon, Filter
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v) => `£${(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;

/* ---- Date period helpers ---- */
const getDateRange = (period) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth(); // 0-indexed

    switch (period) {
        case 'this_month': {
            const start = new Date(y, m, 1);
            return { start_date: start.toISOString(), end_date: now.toISOString() };
        }
        case 'last_month': {
            const start = new Date(y, m - 1, 1);
            const end = new Date(y, m, 0, 23, 59, 59, 999);
            return { start_date: start.toISOString(), end_date: end.toISOString() };
        }
        case 'last_quarter': {
            const qStart = Math.floor(m / 3) * 3;
            const start = new Date(y, qStart - 3, 1);
            const end = new Date(y, qStart, 0, 23, 59, 59, 999);
            return { start_date: start.toISOString(), end_date: end.toISOString() };
        }
        case 'last_quarter_inc': {
            const qStart = Math.floor(m / 3) * 3;
            const start = new Date(y, qStart - 3, 1);
            return { start_date: start.toISOString(), end_date: now.toISOString() };
        }
        case 'all':
        default:
            return { start_date: null, end_date: null };
    }
};

const PERIOD_OPTIONS = [
    { value: 'all', label: 'All Time' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'last_quarter_inc', label: 'Last Quarter + This Month' },
    { value: 'custom', label: 'Custom Range' },
];

const statusConfig = {
    sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Send },
    accepted: { label: 'Accepted', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    declined: { label: 'Declined', color: 'bg-red-100 text-red-700', icon: XCircle },
    expired: { label: 'Expired', color: 'bg-gray-100 text-gray-500', icon: Clock }
};

const StatTile = ({ label, value, sub, icon: Icon, color }) => (
    <div className="p-3 rounded-lg bg-white border border-brand-gray-200 flex items-start gap-3" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
        <div className={`p-2 rounded-md ${color}`}><Icon className="h-4 w-4" /></div>
        <div className="min-w-0">
            <p className="text-xs text-brand-gray-500 uppercase tracking-wide">{label}</p>
            <p className="text-lg font-bold font-heading text-brand-charcoal leading-tight">{value}</p>
            {sub && <p className="text-xs text-brand-gray-400 mt-0.5">{sub}</p>}
        </div>
    </div>
);

/* ---- Overview card for one engineer ---- */
const EngineerCard = ({ eng, onSelect }) => (
    <Card
        className="border border-brand-gray-200 shadow-sm hover:shadow-md hover:border-brand-lime/40 transition-all cursor-pointer"
        onClick={() => onSelect(eng.id)}
        data-testid={`engineer-card-${eng.id}`}
    >
        <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-full bg-brand-lime flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-black" />
                </div>
                <div className="min-w-0">
                    <p className="font-bold text-brand-charcoal">{eng.name}</p>
                    <p className="text-xs text-brand-gray-500 truncate">{eng.email}</p>
                </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                    <p className="text-lg font-bold text-brand-charcoal">{eng.total_quotes}</p>
                    <p className="text-xs text-brand-gray-500">Quotes</p>
                </div>
                <div>
                    <p className="text-lg font-bold text-green-600">{eng.acceptance_rate}%</p>
                    <p className="text-xs text-brand-gray-500">Accepted</p>
                </div>
                <div>
                    <p className="text-lg font-bold text-brand-charcoal">{fmt(eng.revenue)}</p>
                    <p className="text-xs text-brand-gray-500">Revenue</p>
                </div>
            </div>
            <div className="flex justify-between mt-3 pt-3 border-t border-brand-gray-100 text-xs text-brand-gray-500">
                <span>This week: {eng.this_week}</span>
                <span>This month: {eng.this_month}</span>
            </div>
        </CardContent>
    </Card>
);

const EngineerHistory = () => {
    const { token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [engineers, setEngineers] = useState([]);
    const [selectedEngineer, setSelectedEngineer] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [quoteDialog, setQuoteDialog] = useState(null);
    const [viewMode, setViewMode] = useState('table');
    const [datePeriod, setDatePeriod] = useState('all');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');

    const dateParams = useMemo(() => {
        if (datePeriod === 'custom') {
            return {
                start_date: customStart ? new Date(customStart).toISOString() : null,
                end_date: customEnd ? new Date(customEnd + 'T23:59:59.999Z').toISOString() : null,
            };
        }
        return getDateRange(datePeriod);
    }, [datePeriod, customStart, customEnd]);

    useEffect(() => { fetchEngineers(); }, [dateParams]);

    const buildDateQuery = () => {
        const p = new URLSearchParams();
        if (dateParams.start_date) p.set('start_date', dateParams.start_date);
        if (dateParams.end_date) p.set('end_date', dateParams.end_date);
        return p.toString();
    };

    const fetchEngineers = async () => {
        try {
            const dq = buildDateQuery();
            const url = `${API}/admin/engineer-history${dq ? '?' + dq : ''}`;
            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEngineers(res.data);
        } catch { toast.error('Failed to load engineer data'); }
        finally { setLoading(false); }
    };

    const fetchDetail = async (engineerId, status) => {
        setDetailLoading(true);
        try {
            const p = new URLSearchParams();
            if (status && status !== 'all') p.set('status', status);
            if (dateParams.start_date) p.set('start_date', dateParams.start_date);
            if (dateParams.end_date) p.set('end_date', dateParams.end_date);
            const qs = p.toString();
            const res = await axios.get(`${API}/admin/engineer-history/${engineerId}${qs ? '?' + qs : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDetail(res.data);
        } catch { toast.error('Failed to load engineer details'); }
        finally { setDetailLoading(false); }
    };

    const handleSelectEngineer = (id) => {
        setSelectedEngineer(id);
        setStatusFilter('all');
        fetchDetail(id, 'all');
    };

    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        if (selectedEngineer) fetchDetail(selectedEngineer, status);
    };

    // Refetch detail when date params change while viewing an engineer
    useEffect(() => {
        if (selectedEngineer) {
            fetchDetail(selectedEngineer, statusFilter);
        }
    }, [dateParams]);

    const handleDownloadPdf = async (quoteId) => {
        try {
            const res = await axios.get(`${API}/quotes/${quoteId}/pdf`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url; a.download = `quote-${quoteId}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error('Failed to download PDF'); }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="h-8 w-8 animate-spin text-brand-lime" />
            </div>
        );
    }

    /* ---- Date period picker (shared between overview and detail) ---- */
    const DatePeriodFilter = () => (
        <div className="flex flex-wrap items-end gap-2" data-testid="date-period-filter">
            <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-brand-gray-400 hidden sm:block" />
                <Select value={datePeriod} onValueChange={(v) => { setDatePeriod(v); setLoading(true); }}>
                    <SelectTrigger className="h-9 w-[200px]" data-testid="date-period-select">
                        <SelectValue placeholder="Time Period" />
                    </SelectTrigger>
                    <SelectContent>
                        {PERIOD_OPTIONS.map(o => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            {datePeriod === 'custom' && (
                <div className="flex items-center gap-2">
                    <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="h-9 w-[145px]" data-testid="custom-start-date" />
                    <span className="text-xs text-brand-gray-400">to</span>
                    <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="h-9 w-[145px]" data-testid="custom-end-date" />
                </div>
            )}
        </div>
    );

    /* ---- Detail view for a selected engineer ---- */
    if (selectedEngineer && detail) {
        const { engineer: eng, stats, quotes } = detail;
        return (
            <div className="animate-fade-in space-y-6" data-testid="engineer-detail-view">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedEngineer(null); setDetail(null); }} className="self-start" data-testid="back-to-overview-btn">
                        <ArrowLeft className="h-4 w-4 mr-1" /> All Engineers
                    </Button>
                    <div className="flex items-center gap-3 flex-1">
                        <div className="h-12 w-12 rounded-full bg-brand-lime flex items-center justify-center">
                            <User className="h-6 w-6 text-black" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold font-heading text-brand-charcoal">{eng.name}</h1>
                            <p className="text-sm text-brand-gray-500">{eng.email}</p>
                        </div>
                    </div>
                </div>
                <DatePeriodFilter />

                {/* Stats grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatTile label="Total Quotes" value={stats.total_quotes} icon={FileText} color="bg-brand-lime/10 text-brand-lime" />
                    <StatTile label="Acceptance Rate" value={`${stats.acceptance_rate}%`} sub={`${stats.accepted} of ${stats.total_quotes}`} icon={Award} color="bg-green-100 text-green-600" />
                    <StatTile label="Revenue" value={fmt(stats.revenue)} sub="Accepted quotes" icon={PoundSterling} color="bg-emerald-100 text-emerald-600" />
                    <StatTile label="Avg Value" value={fmt(stats.avg_value)} icon={TrendingUp} color="bg-blue-100 text-blue-600" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <StatTile label="Sent" value={stats.sent} icon={Send} color="bg-blue-100 text-blue-600" />
                    <StatTile label="Accepted" value={stats.accepted} icon={CheckCircle} color="bg-green-100 text-green-600" />
                    <StatTile label="Declined" value={stats.declined} icon={XCircle} color="bg-red-100 text-red-600" />
                    <StatTile label="Expired" value={stats.expired} icon={Clock} color="bg-gray-100 text-gray-500" />
                    <StatTile label="This Week" value={stats.this_week} sub={`Month: ${stats.this_month}`} icon={CalendarDays} color="bg-orange-100 text-orange-600" />
                </div>

                {/* Quotes list */}
                <Card className="border-0 shadow-lg">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <CardTitle className="text-base font-bold font-heading">Quote History</CardTitle>
                            <Select value={statusFilter} onValueChange={handleStatusFilter}>
                                <SelectTrigger className="h-9 w-[150px]" data-testid="detail-status-filter">
                                    <SelectValue placeholder="Status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Status</SelectItem>
                                    <SelectItem value="sent">Sent</SelectItem>
                                    <SelectItem value="accepted">Accepted</SelectItem>
                                    <SelectItem value="declined">Declined</SelectItem>
                                    <SelectItem value="expired">Expired</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardHeader>
                    <CardContent className="px-0 pb-0">
                        {detailLoading ? (
                            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-brand-lime" /></div>
                        ) : quotes.length === 0 ? (
                            <div className="text-center py-12 px-6"><FileText className="h-12 w-12 mx-auto text-brand-gray-300 mb-3" /><p className="text-brand-gray-500">No quotes found</p></div>
                        ) : (
                            <div className="divide-y divide-brand-gray-100">
                                {quotes.map(q => {
                                    const S = statusConfig[q.status] || statusConfig.sent;
                                    const packCount = q.items?.length || 1;
                                    return (
                                        <div key={q.id} className="px-4 sm:px-6 py-3 flex items-center gap-3 hover:bg-brand-gray-50 cursor-pointer" onClick={() => setQuoteDialog(q)} data-testid={`history-quote-${q.id}`}>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-medium text-brand-lime">{q.reference_number}</span>
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-sm ${S.color}`}>
                                                        <S.icon className="h-3 w-3" />{S.label}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-brand-charcoal mt-0.5">{q.customer?.company_name || q.customer?.name}</p>
                                                <p className="text-xs text-brand-gray-400">
                                                    {new Date(q.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    {' · '}
                                                    {packCount > 1 ? `${packCount} packs` : (q.items?.[0]?.pack_name || q.spare_pack?.name)}
                                                </p>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <p className="text-sm font-bold">{fmt(q.total_inc_vat || q.total_price)}</p>
                                                <p className="text-xs text-brand-gray-400">Inc VAT</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Quote detail popup */}
                {quoteDialog && (
                    <Dialog open={!!quoteDialog} onOpenChange={() => setQuoteDialog(null)}>
                        <DialogContent className="sm:max-w-[550px]">
                            <DialogHeader>
                                <DialogTitle className="font-heading flex items-center gap-2">
                                    {quoteDialog.reference_number}
                                    {(() => { const S = statusConfig[quoteDialog.status] || statusConfig.sent; return <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-sm ${S.color}`}><S.icon className="h-3 w-3" />{S.label}</span>; })()}
                                </DialogTitle>
                                <DialogDescription>{new Date(quoteDialog.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div className="p-3 bg-brand-gray-50 rounded-lg space-y-1.5">
                                    <div className="flex items-center gap-2 text-sm"><User className="h-4 w-4 text-brand-gray-400" /><span className="font-medium">{quoteDialog.customer?.company_name || quoteDialog.customer?.name}</span></div>
                                    {quoteDialog.customer?.contact_name && <div className="text-xs text-brand-gray-500 pl-6">{quoteDialog.customer.contact_name}</div>}
                                    <div className="flex items-center gap-2 text-xs text-brand-gray-500"><Mail className="h-3 w-3 text-brand-gray-400" />{quoteDialog.customer?.email}</div>
                                </div>
                                <div className="space-y-2">
                                    <p className="text-xs font-bold uppercase tracking-wide text-brand-gray-500">Items</p>
                                    {(quoteDialog.items?.length > 0) ? quoteDialog.items.map((item) => (
                                        <div key={item.pack_id} className="flex items-center justify-between gap-2 text-sm bg-brand-gray-50 rounded px-3 py-2">
                                            <div className="min-w-0">
                                                <span className="font-medium">{item.pack_name}</span>
                                                <span className="text-xs text-brand-gray-400 ml-1">x{item.quantity}</span>
                                            </div>
                                            <span className="font-bold whitespace-nowrap">{fmt(item.line_total_ex_vat)}</span>
                                        </div>
                                    )) : (
                                        <div className="flex items-center gap-2 text-sm"><Package className="h-4 w-4 text-brand-gray-400" /><span>{quoteDialog.spare_pack?.name}</span></div>
                                    )}
                                </div>
                                <div className="pt-2 border-t border-brand-gray-200 space-y-1 text-sm">
                                    <div className="flex justify-between"><span className="text-brand-gray-500">Ex VAT</span><span className="font-medium">{fmt(quoteDialog.total_ex_vat || quoteDialog.total_price)}</span></div>
                                    <div className="flex justify-between"><span className="text-brand-gray-500">VAT</span><span className="font-medium">{fmt(quoteDialog.total_vat || quoteDialog.vat_amount || 0)}</span></div>
                                    <div className="flex justify-between pt-1 border-t border-brand-gray-200"><span className="font-bold">Total Inc VAT</span><span className="text-lg font-bold text-brand-lime">{fmt(quoteDialog.total_inc_vat || quoteDialog.total_price)}</span></div>
                                </div>
                                <Button variant="outline" className="w-full" onClick={() => handleDownloadPdf(quoteDialog.id)} data-testid="download-history-pdf">
                                    <Download className="h-4 w-4 mr-2" /> Download PDF
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                )}
            </div>
        );
    }

    /* ---- Overview: all engineers ---- */
    return (
        <div className="animate-fade-in space-y-6" data-testid="engineer-history-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold font-heading text-brand-charcoal">Engineer History</h1>
                    <p className="text-sm text-brand-gray-500 mt-1">Quote performance and history per engineer</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <DatePeriodFilter />
                    <div className="flex border border-brand-gray-200 rounded-lg overflow-hidden">
                        <button
                            className={`px-3 py-2 flex items-center gap-1.5 text-xs font-medium transition-colors ${viewMode === 'table' ? 'bg-brand-charcoal text-white' : 'bg-white text-brand-gray-500 hover:bg-brand-gray-50'}`}
                            onClick={() => setViewMode('table')}
                            data-testid="view-table-btn"
                        >
                            <TableIcon className="h-3.5 w-3.5" /> Table
                        </button>
                        <button
                            className={`px-3 py-2 flex items-center gap-1.5 text-xs font-medium transition-colors ${viewMode === 'cards' ? 'bg-brand-charcoal text-white' : 'bg-white text-brand-gray-500 hover:bg-brand-gray-50'}`}
                            onClick={() => setViewMode('cards')}
                            data-testid="view-cards-btn"
                        >
                            <LayoutGrid className="h-3.5 w-3.5" /> Cards
                        </button>
                    </div>
                </div>
            </div>

            {engineers.length === 0 ? (
                <div className="text-center py-16">
                    <Users className="h-12 w-12 mx-auto text-brand-gray-300 mb-3" />
                    <p className="text-brand-gray-500">No engineers found</p>
                </div>
            ) : viewMode === 'cards' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {engineers.map(eng => (
                        <EngineerCard key={eng.id} eng={eng} onSelect={handleSelectEngineer} />
                    ))}
                </div>
            ) : (
                <Card className="border-0 shadow-lg rounded-lg overflow-hidden">
                    <CardContent className="p-0">
                        {/* Mobile list */}
                        <div className="md:hidden divide-y divide-brand-gray-100">
                            {engineers.map(eng => (
                                <div key={eng.id} className="px-4 py-3 cursor-pointer active:bg-brand-gray-50" onClick={() => handleSelectEngineer(eng.id)} data-testid={`engineer-row-${eng.id}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-brand-charcoal">{eng.name}</p>
                                            <p className="text-xs text-brand-gray-500">{eng.email}</p>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <p className="text-sm font-bold">{eng.total_quotes} quotes</p>
                                            <p className="text-xs text-green-600">{eng.acceptance_rate}% accepted</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Desktop table */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Engineer</th>
                                        <th>Email</th>
                                        <th className="text-center">Total Quotes</th>
                                        <th className="text-center">Accepted</th>
                                        <th className="text-center">Acceptance Rate</th>
                                        <th className="text-right">Revenue</th>
                                        <th className="text-center">This Week</th>
                                        <th className="text-center">This Month</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {engineers.map(eng => (
                                        <tr key={eng.id} className="cursor-pointer hover:bg-brand-gray-50" onClick={() => handleSelectEngineer(eng.id)} data-testid={`engineer-row-${eng.id}`}>
                                            <td className="font-medium">{eng.name}</td>
                                            <td className="text-brand-gray-500">{eng.email}</td>
                                            <td className="text-center font-bold">{eng.total_quotes}</td>
                                            <td className="text-center text-green-600 font-medium">{eng.accepted}</td>
                                            <td className="text-center">
                                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${eng.acceptance_rate >= 50 ? 'bg-green-100 text-green-700' : eng.acceptance_rate > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                                                    {eng.acceptance_rate}%
                                                </span>
                                            </td>
                                            <td className="text-right font-bold">{fmt(eng.revenue)}</td>
                                            <td className="text-center">{eng.this_week}</td>
                                            <td className="text-center">{eng.this_month}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default EngineerHistory;
